/**
 * File Explorer component for browsing and managing files in a sandbox.
 * Provides file listing, navigation, upload, download, delete, and drag-and-drop capabilities.
 *
 * @module components/terminal/FileExplorer
 */

import { useState, useRef, useCallback, DragEvent } from 'react';
import {
  FolderOpen,
  File,
  ChevronRight,
  ChevronUp,
  Upload,
  Download,
  Trash2,
  FolderPlus,
  RefreshCw,
  Loader2,
  AlertCircle,
  Home,
  FileSymlink,
  Pencil,
} from 'lucide-react';
import {
  useListFiles,
  useDeleteFile,
  useWriteFile,
  useCreateDirectory,
  useDownloadFile,
  fileKeys,
} from '@/hooks';
import { Button, Modal, Input, ConfirmModal } from '@/components/ui';
import { FileEditorModal, MAX_EDIT_FILE_SIZE } from './FileEditorModal';
import type { FileEntry } from '@/types';
import { useQueryClient } from '@tanstack/react-query';

interface FileExplorerProps {
  sandboxId: string;
  /** Optional sandbox group ID — routes file operations through group-scoped URLs */
  sandboxGroupId?: string;
  /** Optional container name for pod sandboxes */
  containerName?: string;
  compact?: boolean;
}

/**
 * Format file size in human-readable format
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Format date in a readable format
 */
function formatDate(dateString: string): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return (
      date.toLocaleDateString() +
      ' ' +
      date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );
  } catch {
    return '-';
  }
}

/**
 * Get file extension for icon styling
 */
function getFileIcon(entry: FileEntry) {
  if (entry.isDir) {
    return <FolderOpen className="h-4 w-4 text-yellow-500" />;
  }
  if (entry.isSymlink) {
    return <FileSymlink className="h-4 w-4 text-cyan-500" />;
  }
  return <File className="h-4 w-4 text-gray-400" />;
}

/**
 * File Explorer component
 */
export function FileExplorer({
  sandboxId,
  sandboxGroupId,
  containerName,
  compact,
}: FileExplorerProps) {
  const [currentPath, setCurrentPath] = useState('/');
  const [selectedFile, setSelectedFile] = useState<FileEntry | null>(null);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useListFiles(
    sandboxId,
    currentPath,
    true,
    containerName,
    sandboxGroupId
  );
  const deleteMutation = useDeleteFile();
  const uploadMutation = useWriteFile();
  const createDirMutation = useCreateDirectory();
  const downloadMutation = useDownloadFile();

  // Navigate to a directory
  const navigateTo = useCallback((path: string) => {
    setCurrentPath(path);
    setSelectedFile(null);
  }, []);

  // Navigate up one directory
  const navigateUp = useCallback(() => {
    if (currentPath === '/') return;
    const parentPath = currentPath.substring(0, currentPath.lastIndexOf('/')) || '/';
    navigateTo(parentPath);
  }, [currentPath, navigateTo]);

  // Navigate to home
  const navigateHome = useCallback(() => {
    navigateTo('/home/user');
  }, [navigateTo]);

  // Handle file/folder click
  const handleEntryClick = useCallback(
    (entry: FileEntry) => {
      if (entry.isDir) {
        navigateTo(entry.path);
      } else {
        setSelectedFile(selectedFile?.path === entry.path ? null : entry);
      }
    },
    [navigateTo, selectedFile]
  );

  // Handle file upload
  const handleUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      for (const file of Array.from(files)) {
        const filePath = `${currentPath}/${file.name}`.replace(/\/+/g, '/');
        await uploadMutation.mutateAsync({
          sandboxId,
          path: filePath,
          content: file,
          createDirs: false,
          containerName,
          sandboxGroupId,
        });
      }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Refresh the file list
      refetch();
    },
    [currentPath, sandboxId, sandboxGroupId, containerName, uploadMutation, refetch]
  );

  // Handle drag and drop file upload
  const uploadFilesToPath = useCallback(
    async (files: FileList, targetPath: string) => {
      for (const file of Array.from(files)) {
        const filePath = `${targetPath}/${file.name}`.replace(/\/+/g, '/');
        await uploadMutation.mutateAsync({
          sandboxId,
          path: filePath,
          content: file,
          createDirs: false,
          containerName,
          sandboxGroupId,
        });
      }
      refetch();
    },
    [sandboxId, sandboxGroupId, containerName, uploadMutation, refetch]
  );

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone entirely
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
      setDragOverFolder(null);
    }
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      setDragOverFolder(null);

      const files = e.dataTransfer.files;
      if (!files || files.length === 0) return;

      // If dropping on a folder, upload there; otherwise, use current path
      const targetPath = dragOverFolder || currentPath;
      await uploadFilesToPath(files, targetPath);
    },
    [currentPath, dragOverFolder, uploadFilesToPath]
  );

  // Handle drag over a specific folder row
  const handleFolderDragOver = useCallback(
    (e: DragEvent<HTMLTableRowElement>, folderPath: string) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOverFolder(folderPath);
    },
    []
  );

  const handleFolderDragLeave = useCallback((e: DragEvent<HTMLTableRowElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolder(null);
  }, []);

  const handleFolderDrop = useCallback(
    async (e: DragEvent<HTMLTableRowElement>, folderPath: string) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      setDragOverFolder(null);

      const files = e.dataTransfer.files;
      if (!files || files.length === 0) return;

      await uploadFilesToPath(files, folderPath);
    },
    [uploadFilesToPath]
  );

  // Handle file download
  const handleDownload = useCallback(async () => {
    if (!selectedFile || selectedFile.isDir) return;

    try {
      const blob = await downloadMutation.mutateAsync({
        sandboxId,
        path: selectedFile.path,
        containerName,
        sandboxGroupId,
      });

      // Create a download link from the blob
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = selectedFile.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      // Error is already handled by the mutation's onError
    }
  }, [selectedFile, sandboxId, sandboxGroupId, containerName, downloadMutation]);

  // Handle delete
  const handleDelete = useCallback(async () => {
    if (!selectedFile) return;

    await deleteMutation.mutateAsync({
      sandboxId,
      path: selectedFile.path,
      recursive: selectedFile.isDir,
      containerName,
      sandboxGroupId,
    });

    setSelectedFile(null);
    setShowDeleteModal(false);
  }, [selectedFile, sandboxId, sandboxGroupId, containerName, deleteMutation]);

  // Handle create folder
  const handleCreateFolder = useCallback(async () => {
    if (!newFolderName.trim()) return;

    const folderPath = `${currentPath}/${newFolderName.trim()}`.replace(/\/+/g, '/');
    await createDirMutation.mutateAsync({
      sandboxId,
      path: folderPath,
      containerName,
      sandboxGroupId,
    });

    setNewFolderName('');
    setShowNewFolderModal(false);
  }, [currentPath, newFolderName, sandboxId, sandboxGroupId, containerName, createDirMutation]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: fileKeys.list(sandboxId, currentPath, containerName),
    });
    refetch();
  }, [queryClient, sandboxId, currentPath, containerName, refetch]);

  // Check if file can be edited (not a directory, not too large)
  const canEditFile = useCallback((file: FileEntry | null): boolean => {
    if (!file || file.isDir || file.isSymlink) return false;
    if (file.size > MAX_EDIT_FILE_SIZE) return false;
    return true;
  }, []);

  // Handle edit button click
  const handleEdit = useCallback(() => {
    if (!selectedFile || !canEditFile(selectedFile)) return;
    setShowEditModal(true);
  }, [selectedFile, canEditFile]);

  // Handle edit save success
  const handleEditSaveSuccess = useCallback(() => {
    // Refresh file list to update modified time
    refetch();
  }, [refetch]);

  // Parse breadcrumb path
  const pathParts = currentPath.split('/').filter(Boolean);
  const breadcrumbs = pathParts.map((part, index) => ({
    name: part,
    path: '/' + pathParts.slice(0, index + 1).join('/'),
  }));

  // Sort entries: directories first, then files, both alphabetically
  const sortedEntries = [...(data?.entries || [])].sort((a, b) => {
    if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <div
      ref={dropZoneRef}
      className={`relative flex flex-col h-full bg-white dark:bg-[#0a0a0a] rounded-lg overflow-hidden border-2 transition-colors ${
        isDragging
          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
          : 'border-gray-200 dark:border-[#2a2a2a]'
      }`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && !dragOverFolder && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary-500/10 dark:bg-primary-500/20 pointer-events-none">
          <div className="flex flex-col items-center gap-2 p-6 bg-white dark:bg-[#161616] rounded-lg shadow-lg border-2 border-dashed border-primary-500">
            <Upload className="h-8 w-8 text-primary-500" />
            <p className="text-sm font-medium text-primary-600 dark:text-primary-400">
              Drop files to upload to {currentPath}
            </p>
          </div>
        </div>
      )}

      {compact ? (
        /* Compact: single merged toolbar + breadcrumb row */
        <div className="flex items-center justify-between gap-2 px-3 py-1 bg-gray-50 dark:bg-[#161616] border-b border-gray-200 dark:border-[#2a2a2a] min-h-0">
          <div className="flex items-center gap-1 overflow-x-auto min-w-0">
            <button
              onClick={navigateHome}
              className="p-1 hover:bg-gray-200 dark:hover:bg-[#2a2a2a] rounded text-gray-600 dark:text-gray-400 shrink-0"
              title="Home"
            >
              <Home className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={navigateUp}
              className="p-1 hover:bg-gray-200 dark:hover:bg-[#2a2a2a] rounded text-gray-600 dark:text-gray-400 shrink-0"
              disabled={currentPath === '/'}
              title="Go up"
            >
              <ChevronUp className="h-3.5 w-3.5" />
            </button>
            <span className="text-gray-400 dark:text-gray-600 mx-0.5 shrink-0">/</span>
            <button
              onClick={() => navigateTo('/')}
              className="text-xs text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 shrink-0"
            >
              root
            </button>
            {breadcrumbs.map((crumb, index) => (
              <div key={crumb.path} className="flex items-center shrink-0">
                <ChevronRight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-600 mx-0.5" />
                <button
                  onClick={() => navigateTo(crumb.path)}
                  className={`text-xs ${
                    index === breadcrumbs.length - 1
                      ? 'text-gray-900 dark:text-gray-100 font-medium'
                      : 'text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
                  }`}
                >
                  {crumb.name}
                </button>
              </div>
            ))}
            {uploadMutation.isPending && (
              <span className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 shrink-0 ml-2">
                <Loader2 className="h-3 w-3 animate-spin" />
                Uploading...
              </span>
            )}
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
              title="Upload file"
            >
              {uploadMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="h-3.5 w-3.5" />
              )}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowNewFolderModal(true)}
              title="New folder"
            >
              <FolderPlus className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleDownload}
              disabled={!selectedFile || selectedFile.isDir || downloadMutation.isPending}
              title="Download selected file"
            >
              {downloadMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleEdit}
              disabled={!canEditFile(selectedFile)}
              title={
                selectedFile && selectedFile.size > MAX_EDIT_FILE_SIZE
                  ? 'File too large to edit (max 1MB)'
                  : 'Edit selected file'
              }
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowDeleteModal(true)}
              disabled={!selectedFile}
              title="Delete selected"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
              title="Refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      ) : (
        /* Full: separate toolbar + breadcrumb rows */
        <>
          {/* Header / Toolbar */}
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-[#161616] border-b border-gray-200 dark:border-[#2a2a2a]">
            <div className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4 text-primary-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                File Explorer
              </span>
              {uploadMutation.isPending && (
                <span className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Uploading...
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadMutation.isPending}
                title="Upload file"
              >
                {uploadMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowNewFolderModal(true)}
                title="New folder"
              >
                <FolderPlus className="h-4 w-4" />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleDownload}
                disabled={!selectedFile || selectedFile.isDir || downloadMutation.isPending}
                title="Download selected file"
              >
                {downloadMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleEdit}
                disabled={!canEditFile(selectedFile)}
                title={
                  selectedFile && selectedFile.size > MAX_EDIT_FILE_SIZE
                    ? 'File too large to edit (max 1MB)'
                    : 'Edit selected file'
                }
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowDeleteModal(true)}
                disabled={!selectedFile}
                title="Delete selected"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleRefresh}
                disabled={isLoading}
                title="Refresh"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Breadcrumb Navigation */}
          <div className="flex items-center gap-1 px-4 py-2 bg-gray-100 dark:bg-[#161616]/50 border-b border-gray-200 dark:border-[#2a2a2a] overflow-x-auto">
            <button
              onClick={navigateHome}
              className="p-1 hover:bg-gray-200 dark:hover:bg-[#2a2a2a] rounded text-gray-600 dark:text-gray-400"
              title="Home"
            >
              <Home className="h-4 w-4" />
            </button>
            <button
              onClick={navigateUp}
              className="p-1 hover:bg-gray-200 dark:hover:bg-[#2a2a2a] rounded text-gray-600 dark:text-gray-400"
              disabled={currentPath === '/'}
              title="Go up"
            >
              <ChevronUp className="h-4 w-4" />
            </button>
            <span className="text-gray-400 dark:text-gray-600 mx-1">/</span>
            <button
              onClick={() => navigateTo('/')}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
            >
              root
            </button>
            {breadcrumbs.map((crumb, index) => (
              <div key={crumb.path} className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-600 mx-1" />
                <button
                  onClick={() => navigateTo(crumb.path)}
                  className={`text-sm ${
                    index === breadcrumbs.length - 1
                      ? 'text-gray-900 dark:text-gray-100 font-medium'
                      : 'text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
                  }`}
                >
                  {crumb.name}
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* File List */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400 p-4">
            <AlertCircle className="h-8 w-8 mb-2 text-red-400" />
            <p className="text-sm text-center">Failed to load files</p>
            <p className="text-xs text-gray-400 dark:text-gray-400 mt-1">
              {(error as Error).message}
            </p>
            <Button variant="secondary" size="sm" onClick={() => refetch()} className="mt-4">
              Try Again
            </Button>
          </div>
        ) : sortedEntries.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400 p-4">
            <Upload className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">This folder is empty</p>
            <p className="text-xs text-gray-400 dark:text-gray-400 mt-1">
              Drag and drop files here to upload
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="sticky top-0 bg-gray-50 dark:bg-[#161616] text-xs text-gray-500 dark:text-gray-400 uppercase">
              <tr>
                <th className={`text-left font-medium ${compact ? 'px-3 py-1' : 'px-4 py-2'}`}>
                  Name
                </th>
                {!compact && <th className="text-right px-4 py-2 font-medium w-24">Size</th>}
                {!compact && <th className="text-right px-4 py-2 font-medium w-40">Modified</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-[#2a2a2a]">
              {sortedEntries.map((entry) => (
                <tr
                  key={entry.path}
                  onClick={() => handleEntryClick(entry)}
                  onDoubleClick={() => entry.isDir && navigateTo(entry.path)}
                  onDragOver={entry.isDir ? (e) => handleFolderDragOver(e, entry.path) : undefined}
                  onDragLeave={entry.isDir ? handleFolderDragLeave : undefined}
                  onDrop={entry.isDir ? (e) => handleFolderDrop(e, entry.path) : undefined}
                  className={`cursor-pointer transition-colors ${
                    dragOverFolder === entry.path
                      ? 'bg-primary-100 dark:bg-primary-900/40 ring-2 ring-primary-500 ring-inset'
                      : selectedFile?.path === entry.path
                        ? 'bg-primary-50 dark:bg-primary-900/20'
                        : 'hover:bg-gray-50 dark:hover:bg-[#161616]/50'
                  }`}
                >
                  <td className={compact ? 'px-3 py-1' : 'px-4 py-2'}>
                    <div className="flex items-center gap-2">
                      {getFileIcon(entry)}
                      <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
                        {entry.name}
                      </span>
                      {entry.isDir && dragOverFolder === entry.path && (
                        <span className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                          Drop to upload here
                        </span>
                      )}
                      {entry.isSymlink && entry.symlinkTarget && (
                        <span className="text-xs text-gray-400 dark:text-gray-400 truncate">
                          -&gt; {entry.symlinkTarget}
                        </span>
                      )}
                    </div>
                  </td>
                  {!compact && (
                    <td className="px-4 py-2 text-right">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {entry.isDir ? '-' : formatFileSize(entry.size)}
                      </span>
                    </td>
                  )}
                  {!compact && (
                    <td className="px-4 py-2 text-right">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(entry.modifiedTime)}
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Hidden file input for uploads */}
      <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleUpload} />

      {/* New Folder Modal */}
      <Modal
        isOpen={showNewFolderModal}
        onClose={() => {
          setShowNewFolderModal(false);
          setNewFolderName('');
        }}
        title="Create New Folder"
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowNewFolderModal(false);
                setNewFolderName('');
              }}
              disabled={createDirMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateFolder}
              isLoading={createDirMutation.isPending}
              disabled={!newFolderName.trim()}
            >
              Create
            </Button>
          </>
        }
      >
        <Input
          label="Folder Name"
          placeholder="new-folder"
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
          autoFocus
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title={`Delete ${selectedFile?.isDir ? 'Folder' : 'File'}`}
        message={`Are you sure you want to delete "${selectedFile?.name}"?${
          selectedFile?.isDir ? ' This will delete all contents recursively.' : ''
        }`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* File Editor Modal */}
      {selectedFile && (
        <FileEditorModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          sandboxId={sandboxId}
          sandboxGroupId={sandboxGroupId}
          containerName={containerName}
          filePath={selectedFile.path}
          fileName={selectedFile.name}
          onSaveSuccess={handleEditSaveSuccess}
        />
      )}
    </div>
  );
}
